import { z } from "zod";
import { RETURN_ORDER_STATES } from "../../../stock/return-order-states";

export const getReturnOrderParamSchema = z.object({
	returnOrderId: z.uuid(),
});

export type GetReturnOrderParamSchema = z.infer<typeof getReturnOrderParamSchema>;

/**
 * `projectId` narrows to one site's returns. `state` narrows to a lifecycle stage, and absent
 * means every state — the same rule the order list follows, so a completed return stays findable.
 */
export const getReturnOrdersQuerySchema = z.object({
	projectId: z.uuid().optional(),
	state: z
		.preprocess(
			(val) => (val === undefined ? undefined : Array.isArray(val) ? val : [val]),
			z.array(z.enum(RETURN_ORDER_STATES)).optional(),
		)
		.optional(),
});

export type GetReturnOrdersQuerySchema = z.infer<typeof getReturnOrdersQuerySchema>;

/**
 * One booking of material back off a site: the article, how much of it arrived, and the trolley it
 * was loaded onto where one was needed.
 *
 * **The article rather than the shelf.** Where it goes is the warehouse's answer, not the
 * returner's — `PutAway.resolveDestinationLocation` picks the assigned location holding the least
 * of that article, so the emptiest shelf fills first and the destination follows the warehouse's
 * current state rather than this project's picking history.
 *
 * `transferCarrierId` is required exactly when the article carries
 * `requiresTransferCarrierOnReturn`, which the server checks rather than trusting: the flag can be
 * set after a gate screen was opened. An article that does not require one may still name it —
 * somebody pushing a trolley past the door is not doing anything wrong.
 *
 * Nothing bounds the quantity. Over-picking is already allowed on the way out, and sites swap
 * material between jobs, so more of an article can come back than that project's orders recorded.
 */
export const postReturnBookingJsonSchema = z.object({
	articleId: z.uuid(),
	quantity: z.coerce.number().positive(),
	transferCarrierId: z.uuid().optional(),
});

export type PostReturnBookingJsonSchema = z.infer<typeof postReturnBookingJsonSchema>;

/** The carrier equivalent: how many of one carrier type came back off the site. */
export const postReturnCarrierBookingJsonSchema = z.object({
	loadCarrierId: z.uuid(),
	quantity: z.coerce.number().int().positive(),
});

export type PostReturnCarrierBookingJsonSchema = z.infer<typeof postReturnCarrierBookingJsonSchema>;

/**
 * The state a caller may move a return order to: signing it off, or calling it off.
 *
 * `open` and `in-progress` are absent because neither is a decision anybody makes. A run starts
 * `open` from the column default, and the first booking moves it to `in-progress` — so offering
 * them here would only provide a way to drag a settled account backwards, which the handler
 * refuses anyway.
 */
export const patchReturnOrderStateJsonSchema = z.object({
	state: z.enum(["complete", "canceled"]),
});

export type PatchReturnOrderStateJsonSchema = z.infer<typeof patchReturnOrderStateJsonSchema>;

export const postReturnOrderVehicleJsonSchema = z.object({
	vehicleId: z.uuid(),
});

export type PostReturnOrderVehicleJsonSchema = z.infer<typeof postReturnOrderVehicleJsonSchema>;

export const deleteReturnOrderVehicleParamSchema = z
	.object({
		vehicleId: z.uuid(),
	})
	.and(getReturnOrderParamSchema);

export type DeleteReturnOrderVehicleParamSchema = z.infer<
	typeof deleteReturnOrderVehicleParamSchema
>;
